import { ExcelLikeGrid } from "../../../shared/excel-grid/components/ExcelLikeGrid";
import type {
  ExcelGridColumn,
  ExcelGridFilterState,
  ExcelGridRow,
} from "../../../shared/excel-grid/models/excel-grid.models";
import { getFilteredRows, hasActiveFilters } from "../../../shared/excel-grid/utils/excel-grid.utils";
import type { AdminCrewReportRunningVesselsTableProps } from "../../models/admin-crew-report.models";
import { toAdminCrewReportRunningVesselGridRows } from "../../utils/admin-crew-report-running-vessels.utils";
import {
  ADMIN_CREW_REPORT_RUNNING_VESSEL_COLUMNS,
  ADMIN_CREW_REPORT_SECTION_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_TEXT_BUTTON_CLASS_NAME,
} from "../../utils/admin-crew-report.constants";
import AdminCrewReportBulkRequestCheckbox from "./AdminCrewReportBulkRequestCheckbox";
import AdminCrewReportBulkRequestHeader from "./AdminCrewReportBulkRequestHeader";
import AdminCrewReportRunningVesselReportAction from "./AdminCrewReportRunningVesselReportAction";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportRunningVesselsTable = ({
  status,
  statistics,
  errorMessage,
  requestMessage,
  requestMessageStatus,
  activeRequestImoNumber,
  reportActionsDisabled,
  requestActionsDisabled,
  requestActionBusy,
  canRequestReports,
  canBulkRequestReports = false,
  bulkRequestControlsDisabled = false,
  selectedBulkRequestImoNumbers,
  onShowReport,
  onRequestReport,
  onBulkRequestSelectionChange,
  onBulkRequest,
}: AdminCrewReportRunningVesselsTableProps) => {
  const [filters, setFilters] = useState<ExcelGridFilterState>({});
  const sectionRef = useRef<HTMLElement>(null);
  const resetFocusFilterKeyRef = useRef<string | null>(null);
  const vessels = useMemo(() => statistics?.vessels ?? [], [statistics?.vessels]);
  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);
  const vesselByImoNumber = useMemo(() => new Map(vessels.map((vessel) => [vessel.imoNumber, vessel])), [vessels]);
  const rows = useMemo(
    () => toAdminCrewReportRunningVesselGridRows(vessels, canRequestReports),
    [canRequestReports, vessels],
  );
  const selectedBulkRequestImoNumbersSet = useMemo(
    () => selectedBulkRequestImoNumbers ?? new Set<string>(),
    [selectedBulkRequestImoNumbers],
  );
  const columnDefinitions = useMemo(
    () =>
      canBulkRequestReports
        ? ADMIN_CREW_REPORT_RUNNING_VESSEL_COLUMNS
        : ADMIN_CREW_REPORT_RUNNING_VESSEL_COLUMNS.filter((column) => column.key !== "bulkRequest"),
    [canBulkRequestReports],
  );
  const filteredRows = useMemo(
    () => getFilteredRows(rows, [...columnDefinitions], filters),
    [columnDefinitions, filters, rows],
  );
  const hasBulkRequestSelection = selectedBulkRequestImoNumbersSet.size > 0;
  const handleBulkRequestSelectionChange = useCallback(
    (imoNumber: string, checked: boolean) => {
      const nextSelection = new Set(selectedBulkRequestImoNumbersSet);
      if (checked) nextSelection.add(imoNumber);
      else nextSelection.delete(imoNumber);
      onBulkRequestSelectionChange?.(nextSelection);
    },
    [onBulkRequestSelectionChange, selectedBulkRequestImoNumbersSet],
  );
  const handleToggleAllBulkRequests = useCallback(() => {
    if (hasBulkRequestSelection) {
      onBulkRequestSelectionChange?.(new Set());
      return;
    }

    onBulkRequestSelectionChange?.(new Set(filteredRows.map((row) => String(row.imoNumber ?? ""))));
  }, [filteredRows, hasBulkRequestSelection, onBulkRequestSelectionChange]);
  const handleOpenBulkRequest = useCallback(() => {
    const selectedVessels = vessels.filter((vessel) => selectedBulkRequestImoNumbersSet.has(vessel.imoNumber));
    if (selectedVessels.length > 0) onBulkRequest?.(selectedVessels);
  }, [onBulkRequest, selectedBulkRequestImoNumbersSet, vessels]);
  const columns = useMemo<ExcelGridColumn[]>(
    () =>
      columnDefinitions.map((column) => ({
        ...column,
        editable: false,
        filterable: column.key !== "bulkRequest",
        sortable: column.key !== "bulkRequest",
        renderHeader:
          column.key === "bulkRequest"
            ? () => (
                <AdminCrewReportBulkRequestHeader
                  controlsDisabled={bulkRequestControlsDisabled}
                  hasSelection={hasBulkRequestSelection}
                  hasFilteredRows={filteredRows.length > 0}
                  onBulkRequest={handleOpenBulkRequest}
                  onToggleAll={handleToggleAllBulkRequests}
                />
              )
            : undefined,
        getSortValue:
          column.key === "reportDate"
            ? (row: ExcelGridRow) => vesselByImoNumber.get(String(row.imoNumber ?? ""))?.reportReceivedAt ?? ""
            : undefined,
        renderCell:
          column.key === "bulkRequest"
            ? (row: ExcelGridRow) => {
                const vessel = vesselByImoNumber.get(String(row.imoNumber ?? ""));
                if (!vessel) return null;

                return (
                  <AdminCrewReportBulkRequestCheckbox
                    checked={selectedBulkRequestImoNumbersSet.has(vessel.imoNumber)}
                    disabled={bulkRequestControlsDisabled}
                    vessel={vessel}
                    onChange={handleBulkRequestSelectionChange}
                  />
                );
              }
            : column.key === "report"
              ? (row: ExcelGridRow) => {
                  const vessel = vesselByImoNumber.get(String(row.imoNumber ?? ""));
                  if (!vessel || row.report === "No report") return String(row.report ?? "");

                  return (
                    <AdminCrewReportRunningVesselReportAction
                      vessel={vessel}
                      activeRequestImoNumber={activeRequestImoNumber}
                      reportActionsDisabled={reportActionsDisabled}
                      requestActionsDisabled={requestActionsDisabled}
                      requestActionBusy={requestActionBusy}
                      onShowReport={onShowReport}
                      onRequestReport={onRequestReport}
                    />
                  );
                }
              : column.key === "maxPeriod" || column.key === "above11Months" || column.key === "category"
                ? (row: ExcelGridRow) => {
                    const vessel = vesselByImoNumber.get(String(row.imoNumber ?? ""));
                    const value = String(row[column.key] ?? "");
                    if (!value) return null;

                    const shouldEmphasize =
                      (column.key === "maxPeriod" && vessel?.hasAbove11Months === true) ||
                      (column.key === "above11Months" && vessel?.hasAbove11Months === true) ||
                      (column.key === "category" &&
                        (vessel?.category === "FROM_11_TO_13_MONTHS" || vessel?.category === "ABOVE_13_MONTHS"));

                    return (
                      <span
                        className={twMerge(
                          "font-normal",
                          shouldEmphasize && "font-bold text-[#BF0002] dark:text-red-300",
                        )}
                      >
                        {value}
                      </span>
                    );
                  }
                : undefined,
      })),
    [
      activeRequestImoNumber,
      bulkRequestControlsDisabled,
      columnDefinitions,
      filteredRows.length,
      handleBulkRequestSelectionChange,
      handleOpenBulkRequest,
      handleToggleAllBulkRequests,
      hasBulkRequestSelection,
      onRequestReport,
      onShowReport,
      requestActionBusy,
      reportActionsDisabled,
      requestActionsDisabled,
      selectedBulkRequestImoNumbersSet,
      vesselByImoNumber,
    ],
  );

  const handleResetFilters = useCallback(() => {
    resetFocusFilterKeyRef.current =
      columnDefinitions.find((column) => column.key !== "bulkRequest" && (filters[column.key] ?? "").trim().length > 0)
        ?.key ?? null;
    setFilters({});
  }, [columnDefinitions, filters]);

  useEffect(() => {
    const filterKey = resetFocusFilterKeyRef.current;
    if (hasFilters || !filterKey) return;

    resetFocusFilterKeyRef.current = null;
    const filterInput = sectionRef.current?.querySelector<HTMLInputElement>(
      `[data-test="admin-crew-report-running-vessels-grid-filter-${filterKey}"]`,
    );
    filterInput?.focus({ preventScroll: true });
    filterInput?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [hasFilters]);

  return (
    <section
      ref={sectionRef}
      aria-label="Running vessel crew reports"
      data-test="admin-crew-report-running-vessels-section"
      className="min-w-0 bg-white p-4 shadow-sm dark:bg-gray-800"
    >
      <header className="mb-4 flex min-h-9 items-center justify-between gap-3">
        <h2 className={twMerge(ADMIN_CREW_REPORT_SECTION_TITLE_CLASS_NAME, "mb-0 whitespace-nowrap")}>
          Vessels Information
        </h2>
        {hasFilters ? (
          <button
            type="button"
            aria-label="Reset vessel filters"
            data-test="admin-crew-report-running-vessels-reset-filters"
            className={ADMIN_CREW_REPORT_TEXT_BUTTON_CLASS_NAME}
            onClick={handleResetFilters}
          >
            Reset
          </button>
        ) : null}
      </header>
      {requestMessage ? (
        <p
          data-test="admin-crew-report-running-vessels-request-message"
          className={twMerge(
            "mb-3 text-sm font-medium",
            requestMessageStatus === "error"
              ? "text-[#BF0002]"
              : requestMessageStatus === "sentWithWarning"
                ? "text-[#8A5A00] dark:text-amber-300"
                : "text-green",
          )}
          role={requestMessageStatus === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          {requestMessage}
        </p>
      ) : null}

      {status === "loading" ? (
        <div className="flex min-h-[160px] items-center justify-center text-sm text-[#4A5655] dark:text-gray-300">
          Loading running vessel crew reports.
        </div>
      ) : status === "error" ? (
        <div className="flex min-h-[160px] items-center justify-center text-sm font-medium text-[#BF0002]" role="alert">
          {errorMessage || "Running vessel crew reports could not be loaded."}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-[160px] items-center justify-center text-sm text-[#4A5655] dark:text-gray-300">
          No running vessels are available.
        </div>
      ) : (
        <div data-test="admin-crew-report-running-vessels-grid-viewport" className="min-h-[600px]">
          <ExcelLikeGrid
            dataTest="admin-crew-report-running-vessels-grid"
            rows={rows}
            columns={columns}
            emptyLabel="No running vessels are available."
            rowHeight={34}
            showHeadersWhenEmpty
            virtualized
            virtualizedMaxHeight={600}
            useContentColumnWidths={false}
            editingDisabled
            useTabForCellNavigation={false}
            filterState={filters}
            onFilterStateChange={setFilters}
            className="focus:border-greyscale-20 max-h-[600px] [scrollbar-width:thin] dark:focus:border-gray-700 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&_td]:whitespace-nowrap [&_td]:break-normal [&_td]:!text-center [&_td]:!text-[11px] [&_th]:text-center [&_th_button]:justify-center [&_th_button]:text-center [&_th_input]:text-center"
            onCellCommit={() => undefined}
          />
        </div>
      )}
    </section>
  );
};

export default AdminCrewReportRunningVesselsTable;
